if (!customElements.get('localization-popup')) {
  customElements.define(
    'localization-popup',
    class LocalizationPopup extends HTMLElement {
      constructor() {
        super();
        this.dialog = this.querySelector('.localization-popup__dialog');
        this.countrySelect = this.querySelector('select[name="country_code"]');
        this.languageSelect = this.querySelector('select[name="locale_code"]');
        this.form = this.querySelector('form');
        this.textElement = this.querySelector('.localization-popup__text');
        this.suggestedCountry = null;
        this.onKeyUp = this.onKeyUp.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onSectionSelect = this.onSectionSelect.bind(this);
        this.onSectionDeselect = this.onSectionDeselect.bind(this);
      }

      connectedCallback() {
        this.querySelectorAll('[data-popup-close]').forEach((button) =>
          button.addEventListener('click', () => this.close(true))
        );
        if (this.form) this.form.addEventListener('submit', () => this.saveDismissal());
        document.addEventListener('click', this.onDocumentClick);

        if (window.Shopify && window.Shopify.designMode) {
          document.addEventListener('shopify:section:select', this.onSectionSelect);
          document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
          return;
        }
        this.init();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('shopify:section:select', this.onSectionSelect);
        document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
        document.removeEventListener('keyup', this.onKeyUp);
      }

      async init() {
        // Mode test : ?localization_popup dans l'URL force l'ouverture,
        // sans tenir compte du délai de réaffichage et sans l'enregistrer.
        if (new URLSearchParams(window.location.search).has('localization_popup')) {
          this.testMode = true;
          const suggestion = await this.fetchSuggestion();
          if (suggestion && this.applyCountrySuggestion(suggestion.country)) {
            this.applyLanguageSuggestion(suggestion.language);
          } else {
            this.renderGenericText();
          }
          this.open();
          return;
        }

        if (this.wasDismissedRecently()) return;
        const suggestion = await this.fetchSuggestion();
        if (!suggestion || !this.applyCountrySuggestion(suggestion.country)) return;
        this.applyLanguageSuggestion(suggestion.language);
        const delay = parseInt(this.dataset.delay, 10) || 0;
        setTimeout(() => this.open(), delay * 1000);
      }

      async fetchSuggestion() {
        let root = this.dataset.rootUrl || '/';
        if (!root.endsWith('/')) root += '/';
        const params = new URLSearchParams({
          'country[enabled]': 'true',
          'country[exclude]': this.dataset.currentCountry,
          'language[enabled]': 'true',
          'language[exclude]': this.dataset.currentLanguage,
        });
        try {
          const response = await fetch(`${root}browsing_context_suggestions.json?${params}`);
          if (!response.ok) return null;
          const data = await response.json();
          const parts =
            (data && data.suggestions && data.suggestions[0] && data.suggestions[0].parts) || null;
          if (!parts) return null;
          return { country: parts.country || null, language: parts.language || null };
        } catch (error) {
          return null;
        }
      }

      applyCountrySuggestion(country) {
        if (!country || !country.handle || !this.countrySelect) return false;
        const option = this.countrySelect.querySelector(
          `option[value="${CSS.escape(country.handle)}"]`
        );
        if (!option) return false;
        this.countrySelect.value = country.handle;
        this.suggestedCountry = country.handle;
        this.renderText(country.name || option.dataset.name || option.value);
        return true;
      }

      applyLanguageSuggestion(language) {
        if (!language || !language.handle || !this.languageSelect) return;
        const option = this.languageSelect.querySelector(
          `option[value="${CSS.escape(language.handle)}"]`
        );
        if (option) this.languageSelect.value = language.handle;
      }

      renderText(countryName) {
        if (!this.textElement) return;
        const [before, after] = (this.dataset.text || '').split('[country]');
        const strong = document.createElement('strong');
        strong.textContent = countryName;
        this.textElement.replaceChildren(before || '', strong, after || '');
      }

      renderGenericText() {
        if (this.textElement) this.textElement.textContent = this.dataset.textGeneric || '';
      }

      open() {
        if (!this.hasAttribute('hidden')) return;
        this.openedBy = document.activeElement;
        this.removeAttribute('hidden');
        document.addEventListener('keyup', this.onKeyUp);
        if (typeof trapFocus === 'function') {
          trapFocus(this.dialog);
        } else if (this.dialog) {
          this.dialog.focus();
        }
        this.saveDismissal();
      }

      openFromTrigger() {
        if (!this.suggestedCountry) this.renderGenericText();
        this.open();
      }

      close(save) {
        if (this.hasAttribute('hidden')) return;
        this.setAttribute('hidden', '');
        document.removeEventListener('keyup', this.onKeyUp);
        if (typeof removeTrapFocus === 'function') removeTrapFocus(this.openedBy);
        if (save) this.saveDismissal();
      }

      onDocumentClick(event) {
        const trigger = event.target.closest('[data-localization-popup-trigger]');
        if (!trigger) return;
        event.preventDefault();
        this.openFromTrigger();
      }

      onKeyUp(event) {
        if (event.code && event.code.toUpperCase() === 'ESCAPE') this.close(true);
      }

      onSectionSelect(event) {
        if (event.detail.sectionId !== this.dataset.sectionId) return;
        if (!this.suggestedCountry) this.applyPreviewFallback();
        this.open();
      }

      onSectionDeselect(event) {
        if (event.detail.sectionId === this.dataset.sectionId) this.close(false);
      }

      applyPreviewFallback() {
        const options = this.countrySelect ? Array.from(this.countrySelect.options) : [];
        const option = options.find((opt) => opt.value !== this.dataset.currentCountry);
        if (!option) return;
        this.countrySelect.value = option.value;
        this.suggestedCountry = option.value;
        this.renderText(option.dataset.name || option.text);
      }

      get storageKey() {
        return 'localization-popup';
      }

      wasDismissedRecently() {
        try {
          const raw = window.localStorage.getItem(this.storageKey);
          if (!raw) return false;
          const days = parseInt(this.dataset.dismissDays, 10) || 30;
          const savedAt = JSON.parse(raw).at || 0;
          return Date.now() - savedAt < days * 24 * 60 * 60 * 1000;
        } catch (error) {
          return false;
        }
      }

      saveDismissal() {
        if (this.testMode) return;
        if (window.Shopify && window.Shopify.designMode) return;
        try {
          window.localStorage.setItem(
            this.storageKey,
            JSON.stringify({ at: Date.now(), country: this.suggestedCountry })
          );
        } catch (error) {
          // localStorage indisponible (navigation privée) : le popup pourra se réafficher.
        }
      }
    }
  );
}
